import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayableStatus,
  PayeeEngagementStatus
} from "@wingspanhq/wingspan-api-sdk";
import queryString from "qs";
import { useHistory } from "react-router-dom";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  queriesPayableRows,
  queriesPayeeEngagements,
  queriesPayees
} from "@query";
import { useWSMutation } from "@ws-react-query";
import { payeeEngagementsApi } from "@api";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";
import { ContractorInformation } from "./ContractorInformation";

/**
 * Bulk archive. v1 PATCHed `status: Inactive` on each engagement; v3 moves the
 * lifecycle to action verbs, so each one is retired here before the payee is
 * deactivated — `deletePayee` is refused (409) while any live assignment
 * remains.
 *
 * The verb depends on the assignment's state, because `suspend` is only valid
 * from `Activated`:
 *   - `Activated` -> `suspend` (not `terminate`: terminal states cannot
 *     transition and the restore flow must stay open);
 *   - `Created`   -> `delete`. It was never active, so there is nothing to
 *     suspend — the API answers `409 InvalidLifecycleTransition` — and a
 *     never-activated assignment carries no history worth keeping.
 *
 * Probed against the live API (see
 * `src/v3/api/tests/specs/payeeEngagementLifecycleProbe.itest.ts`):
 *   suspend(from Created)                            -> 409 InvalidLifecycleTransition
 *   deletePayee(with untouched Created engagement)   -> 409 ResourceConflict
 *   deletePayeeEngagement(Created)                   -> OK
 *   deletePayee(after deleting it)                   -> OK
 * Suspending everything indiscriminately is what left every newly-created
 * contractor un-archivable.
 */
const useMutationSuspendPayeeEngagements = () =>
  useWSMutation(
    async (request: {
      payeeId: string;
      engagements: { id: string; status?: PayeeEngagementStatus }[];
    }) =>
      Promise.all(
        request.engagements.map(engagement =>
          engagement.status === PayeeEngagementStatus.CREATED
            ? payeeEngagementsApi.deletePayeeEngagement({
                payeeId: request.payeeId,
                payeeEngagementId: engagement.id
              })
            : payeeEngagementsApi.suspendPayeeEngagement({
                payeeId: request.payeeId,
                payeeEngagementId: engagement.id
              })
        )
      ),
    {
      dependencies: [
        queriesPayeeEngagements.listPayeeEngagements.queryKeyBase,
        queriesPayeeEngagements.getPayeeEngagement.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase
      ]
    }
  );

export interface ArchiveContractorModalProps {
  payeeId: string;
  onClose: () => void;
}

export const ArchiveContractorModal: React.FC<ArchiveContractorModalProps> = ({
  payeeId,
  onClose
}) => {
  const history = useHistory();
  const { openSnackbar } = useWSSnackbar();
  // Archive → v3 `deletePayee` (soft-deactivate → `Deactivated`). Restore has no
  // v3 activate endpoint, so the row "Restore" action stays on v1. See
  // V3_MIGRATION_TRACKER.md (payees).
  const xWingspanAccount = useXWingspanPayerId();
  const [archivePayee, archivePayeeMeta] =
    queriesPayees.deletePayee.useMutation();
  const [archiveEngagements, archiveEngagementsMeta] =
    useMutationSuspendPayeeEngagements();

  const outstandingPayablesQuery =
    queriesPayableRows.searchPayableRows.useQuery({
      filter: {
        metadata_invoiceType: { ne: "approvedInvoicesPayment" },
        metadata_creationSource: { ne: "personalPaylink" },
        payeeId: { eq: payeeId },
        status: { anyOf: [PayableStatus.PENDING, PayableStatus.OPENED] }
      },
      page: { size: 1 }
    });
  const queryPayeeEngagements =
    queriesPayeeEngagements.listPayeeEngagements.useQueryAll({
      payeeId,
      xWingspanAccount
    });

  const engagements = queryPayeeEngagements.flatData;

  const hasOutstandingInvoices = !!outstandingPayablesQuery.data?.data?.length;

  // v1 `Inactive` → v3 `Suspended` (the app's archive) plus the terminal `Terminated`.
  const visibleEngagements = engagements?.filter(
    e =>
      e.status !== PayeeEngagementStatus.SUSPENDED &&
      e.status !== PayeeEngagementStatus.TERMINATED
  );

  return (
    <WSList gap="L">
      <WSText.ParagraphSm weight="book">
        Are you sure you want to archive this contractor?
      </WSText.ParagraphSm>

      <WSPanel
        shimmer={
          queryPayeeEngagements.isLoading || outstandingPayablesQuery.isLoading
        }
      >
        <ContractorInformation payeeId={payeeId} />
        {visibleEngagements?.length ? (
          <WSElement mt="2XL">
            <WSText.ParagraphSm color="gray500">
              {`${visibleEngagements.length} engagement${
                visibleEngagements.length === 1 ? "" : "s"
              } will be deactivated:`}
            </WSText.ParagraphSm>
            {visibleEngagements.slice(0, 5).map((e, index) => (
              <WSText.ParagraphSm mt="S" key={e.id}>
                {e.engagementName ?? undefined}
              </WSText.ParagraphSm>
            ))}
            {visibleEngagements.length > 5 ? (
              <WSText.ParagraphSm mt="S" color="gray500">
                + {visibleEngagements.length - 5} more
              </WSText.ParagraphSm>
            ) : null}
          </WSElement>
        ) : null}
      </WSPanel>

      <WSAlert
        theme="info"
        icon="info-circle"
        size="M"
        title="Archiving contractor"
      >
        After a contractor is archived, no new payments can be created or sent
        to the contractor. The contractor will be made archived and hidden from
        engagements they were on.
        <WSElement mt="M" />
        Existing invoices and the contractor profile will remain visible until
        you delete them. You can always restore the contractor when needed. The
        contractor’s eligibility status will be recalculated upon restore.
      </WSAlert>

      {hasOutstandingInvoices && (
        <WSAlert
          theme="warning"
          icon="alert"
          size="M"
          title="Outstanding invoices exist"
        >
          The contractor cannot be archived at this time. All outstanding
          invoices for this contractor must be resolved before archiving the
          contractor.{" "}
          <WSText.ParagraphSm
            inline
            color="blue400"
            onClick={() => {
              const query = queryString.stringify({
                payeeId
              });
              history.push(`/member/invoices/payables/all?${query}`);
            }}
          >
            See all
          </WSText.ParagraphSm>
        </WSAlert>
      )}

      <ErrorAlert error={archivePayeeMeta.error} context="ArchiveClient" />

      <WSActions
        alignment="fill"
        disabled={
          !outstandingPayablesQuery.isSuccess ||
          !queryPayeeEngagements.isSuccess
        }
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Archive contractor",
            loading: archivePayeeMeta.isLoading,
            disabled: hasOutstandingInvoices,
            onAsyncClick: async () => {
              if (visibleEngagements?.length) {
                await archiveEngagements({
                  payeeId,
                  engagements: visibleEngagements.map(e => ({
                    id: e.id as string,
                    status: e.status
                  }))
                });
              }

              await archivePayee(
                {
                  payeeId,
                  xWingspanAccount
                },
                {
                  onSuccess: () => {
                    openSnackbar({
                      duration: 5000,
                      type: "success",
                      message: "Contractor archived successfully"
                    });
                    onClose();
                  },
                  onError: () => {
                    openSnackbar({
                      duration: 5000,
                      type: "warning",
                      message: `Failed to archive contractor`
                    });
                  }
                }
              );
            }
          }
        ]}
      />
    </WSList>
  );
};

export const useArchiveContractorModal = () =>
  useWSModal(ArchiveContractorModal, {
    title: "Archive contractor",
    size: "M"
  });
